import { useCallback, useEffect, useState } from 'react';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { CourierUserApi } from '@shared/sdk-v1/apis';
import type { PasskeyCredential } from '@shared/sdk-v1/models';
import { apiConfig } from '../../shared/api-config';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { extractApiError } from '../../utils/apiError';

const GENERIC_ERROR = 'Something went wrong with your passkeys. Please try again.';

/** Cancelling the browser sheet is a deliberate action, not a failure. */
const SILENT_ERROR_NAMES = ['NotAllowedError', 'AbortError'];

/**
 * Enrol, rename and revoke the signed-in user's passkeys.
 *
 * The list is re-fetched after every mutation rather than patched locally:
 * the server owns the credential's identity (and re-enrolling an existing
 * authenticator updates the row in place instead of adding one).
 */
export const usePasskeyManagement = () => {
  const [credentials, setCredentials] = useState<PasskeyCredential[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported = browserSupportsWebAuthn();

  const clearError = useCallback(() => setError(null), []);

  const handleError = useCallback(async (err: unknown) => {
    if (SILENT_ERROR_NAMES.includes((err as Error)?.name)) return;

    const { message } = await extractApiError(err, GENERIC_ERROR);
    setError(message);
    showErrorToast({ title: 'Passkeys', message });
  }, []);

  const load = useCallback(async () => {
    if (!isSupported) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const api = new CourierUserApi(apiConfig());
      const response = await api.courierUserPasskeyList();
      setCredentials(response?.credentials || []);
      setError(null);
    } catch (err) {
      await handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [handleError, isSupported]);

  useEffect(() => {
    load();
  }, [load]);

  const addPasskey = async (name?: string) => {
    if (!isSupported) return { success: false };

    setError(null);
    setIsBusy(true);

    try {
      const api = new CourierUserApi(apiConfig());
      const { options } = await api.courierUserPasskeyRegisterOptions();
      const credential = await startRegistration(options as never);

      await api.courierUserPasskeyRegisterVerify({
        credential: credential as never,
        ...(name ? { name } : {}),
      });

      showSuccessToast({ title: 'Passkeys', message: 'Passkey added.' });
      await load();
      return { success: true };
    } catch (err) {
      await handleError(err);
      return { success: false };
    } finally {
      setIsBusy(false);
    }
  };

  const rename = async (credentialId: string, name: string) => {
    setError(null);
    setIsBusy(true);

    try {
      const api = new CourierUserApi(apiConfig());
      await api.courierUserPasskeyRename({ credentialId, name });
      await load();
      return { success: true };
    } catch (err) {
      await handleError(err);
      return { success: false };
    } finally {
      setIsBusy(false);
    }
  };

  const remove = async (credentialId: string) => {
    setError(null);
    setIsBusy(true);

    try {
      const api = new CourierUserApi(apiConfig());
      await api.courierUserPasskeyDelete({ credentialId });
      showSuccessToast({ title: 'Passkeys', message: 'Passkey removed.' });
      await load();
      return { success: true };
    } catch (err) {
      await handleError(err);
      return { success: false };
    } finally {
      setIsBusy(false);
    }
  };

  return {
    credentials,
    isSupported,
    isLoading,
    isBusy,
    error,
    clearError,
    load,
    addPasskey,
    rename,
    remove,
  };
};
